import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllSilos } from '../../services/Silo/siloService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreateSiloModal from '../Silo/CreateSiloModal';

const SiloInput = ({ value, onChange, label, isInvalid, userRoles, createdNew }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedSilo, setSelectedSilo] = useState(value || { id: null, name: '' });
  const [searchText, setSearchText] = useState(value?.name || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [silos, setSilos] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedSilo(value || { id: null, name: '' });
    setSearchText(value?.name || '');
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchSilos = useCallback(async () => {
    if (!shouldFetch) return;
    try {
      const data = await getAllSilos(filterParams, page - 1, pageSize);
      setSilos(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar silos:', error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchSilos();
    }
  }, [page, pageSize, filterParams, fetchSilos, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filter = { description: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectSilo = (silo) => {
    setSelectedSilo(silo);
    setSearchText(silo.name);
    setIsReadOnly(true);
    onChange({ name: silo.name, id: silo.id });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedSilo({ id: null, name: '' });
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
  };

  const handleCloseModal = () => {
    if (!selectedSilo.id) {
      setSearchText('');
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecione um silo"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Silo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', display: createdNew ? 'block' : 'none' }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>

          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Capacidade</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {silos.map((silo) => (
                <tr key={silo.id}>
                  <td>{silo.name}</td>
                  <td>{silo.capacity.toLocaleString('pt-BR')}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectSilo(silo)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={silos.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <CreateSiloModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedSilo(created);
          onChange(created);
        }}
      />
    </>
  );
};

export default SiloInput;